import { useState } from "react"
import styled from "styled-components"

import { colors } from "@minuthq/meatball-ui-react"

import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { ParadiseUserApiClientSecret } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserApiClients/ParadiseUserApiClientSecret"
import { IdWrapper } from "src/components/Paradise/sharedStyles"
import { useFetchApiClients } from "src/data/apiClients/apiClientQueries"
import { OwnerType } from "src/data/integrations/types/integrationTypes"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { CopyText } from "src/ui/CopyText/CopyText"
import InfoIcon from "src/ui/icons/info.svg"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MBanner } from "src/ui/MBanner/MBanner"
import { MTooltip } from "src/ui/MTooltip/MTooltip"
import { spacing } from "src/ui/spacing"

const LIMIT = 15

export function ParadiseUserApiClients({ userId }: { userId: string }) {
  const { navigate } = useRouter()

  const [offset, setOffset] = useState(0)

  const fetchedUserApiClients = useFetchApiClients({
    owner_type: OwnerType.USER,
    owner_id: userId,
    limit: LIMIT,
    offset,
    options: {
      keepPreviousData: true,
    },
  })

  const headers = [
    <div key={"name"}>Name</div>,
    <div key={"created-at"}>Created at</div>,
    <div key={"client-id"}>Client id</div>,
    <div key={"client-secret"}>Client secret</div>,
  ]

  const rows =
    fetchedUserApiClients.data?.clients.map((client) => (
      <>
        <div>
          <IdWrapper>
            <div>{client.name}</div>
            {client.deleted && (
              <MTooltip title="Deleted">
                <InfoIcon width={12} fill={colors.systemError} />
              </MTooltip>
            )}
          </IdWrapper>
        </div>
        <div>{client.created_at}</div>
        <div>
          <ClientBox>
            <InternalLink
              to={Routes.ParadiseClient.location(client.client_id)}
              onClick={(e) => e.stopPropagation()}
            >
              {client.client_id}
            </InternalLink>
            <CopyText
              value={client.client_id}
              copiedText="Copied!"
              width={20}
            />
          </ClientBox>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <ParadiseUserApiClientSecret clientId={client.client_id} />
        </div>
      </>
    )) ?? []

  return (
    <div>
      <MBanner type="info">
        These are legacy API clients, please visit the organization if you don't
        find the API client here
        <div>
          <InternalLink to={Routes.ParadiseUser.location(userId)}>
            All organizations
          </InternalLink>
        </div>
      </MBanner>
      <ParadiseTable
        header={headers}
        rows={rows}
        onRowClick={(index) => {
          if (fetchedUserApiClients.data?.clients) {
            navigate(
              Routes.ParadiseClient.location(
                // @ts-expect-error: noUncheckedIndexedAccess
                fetchedUserApiClients.data.clients[index].client_id
              )
            )
          }
        }}
        error={{
          hasError: fetchedUserApiClients.isError,
          title: fetchedUserApiClients.error?.message,
        }}
      />
      <ParadisePager
        limit={LIMIT}
        offset={offset}
        setOffset={setOffset}
        totalCount={fetchedUserApiClients.data?.paging.total_count}
      />
    </div>
  )
}

const ClientBox = styled.div`
  display: flex;
  gap: ${spacing.S};
  align-items: center;
`
